import { DynamicForm } from "@shared/components/DynamicForm";
import { toast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import React, { useEffect } from "react";
import { useCreateSystemType, useUpdateSystemType } from "../../hooks";

interface SystemType {
  code: string;
  name: string;
  description: string;
  parameters: any[];
}

interface SystemTypeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: SystemType | null;
  searchTerm?: string;
}

export const SystemTypeDialog: React.FC<SystemTypeDialogProps> = ({
  isOpen,
  onClose,
  editData = null,
  searchTerm,
}) => {
  const remoteUtilityId = getRemoteUtilityId();
  const createSystemType = useCreateSystemType(searchTerm);
  const updateSystemType = useUpdateSystemType();
  const isEditMode = Boolean(editData);

  // Form fields definition
  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "System Type Name",
      placeholder: "Enter system type name...",
      required: true,
      className: "col-span-1 mt-3",
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      placeholder: "Enter system type description...",
      rows: 3,
      required: false,
      className: "col-span-1 mt-3",
    },
  ];

  // Create form with proper default values
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      description: "",
    },
  });

  // Populate form when edit data changes or dialog opens
  useEffect(() => {
    if (isOpen) {
      const initialValues = {
        name: editData?.name || "",
        description: editData?.description || "",
      };

      // Use FormService.populateForm for proper form population
      FormService.populateForm(form, initialValues, formFields);
    }
  }, [isOpen, editData]);

  // Handle form submission
  const handleSubmit = async (formValues: any) => {
    try {
      const operation = isEditMode ? "update" : "create";

      if (operation === "update") {
        const payload = {
          ...editData,
          remote_utility_id: remoteUtilityId,
          code: editData?.code,
          name: formValues.name,
          description: formValues.description,
        };
        await updateSystemType.mutateAsync(payload);
        toast({
          title: "System Type Updated",
          description: "System type has been updated successfully.",
        });
      } else {
        const payload = {
          code: formValues.name,
          name: formValues.name,
          description: formValues.description,
          remote_utility_id: remoteUtilityId,
        };
        await createSystemType.mutateAsync(payload);
      }

      handleClose();
    } catch (error) {
      toast({
        title: `Failed to create system type: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    form.reset({
      name: "",
      description: "",
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit System Type" : "Add New System Type"}
          </DialogTitle>
          <DialogDescription className="mb-5">
            {isEditMode
              ? "Update the system type name and description"
              : "Create a new system type with name and description"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleClose}
            gridColumns={1}
            responsiveColumns={{
              sm: 1,
              md: 1,
            }}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: isEditMode
                ? "Update System Type"
                : "Add System Type",
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: {
                form: "space-y-6 p-0",
                button: "px-4 py-2 rounded-md font-medium transition-colors",
                buttonContainer:
                  "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                label: "block text-sm font-medium text-gray-700 mb-1",
                error: "text-sm text-red-600 mt-1",
                helperText: "text-sm text-gray-500 mt-1",
                container: "space-y-1",
              },
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
